import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_GROUP_MEMORY_RULES, DEFAULT_GROUP_SKILL_RULES, type GroupInput, type GroupKnowledgeInput, type Permissions, type Quotas, type ShareLevel } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useGroups, usePublicConfig } from "../hooks.ts";
import { Badge, Button, Card, cx, ErrorText, Field, Input, segItem, Spinner, Switch, useActiveInView } from "../components/ui.tsx";
import { LEVEL_LABEL, LevelPicker } from "../components/knowledge.tsx";
import { GroupBriefingForm } from "./Briefings.tsx";
import { Icon } from "../components/icons.tsx";

export type Group = NonNullable<ReturnType<typeof useGroups>["data"]>[number];

/** What members' assistants may use: the Access tab's switches, and the badges on the Groups page. */
export const CAPABILITIES: { key: keyof Permissions; label: string; hint: string }[] = [
  { key: "terminal", label: "Terminal & code", hint: "Run commands and code in the user's own sandbox, with every tool installed there" },
  { key: "browser", label: "Browser", hint: "Drive a browser, and sign in to sites in it with the person's help" },
  { key: "webTools", label: "Web search & fetch", hint: "Search the web and read pages" },
  { key: "cronJobs", label: "Scheduled jobs", hint: "Create recurring tasks" },
  { key: "subagents", label: "Subagents", hint: "Split work across helper agents — multiplies AI cost" },
  { key: "fileUpload", label: "File uploads", hint: "Upload files into chat and Files" },
  { key: "colleagueRequests", label: "Colleague requests", hint: "Their assistant may ask colleagues' assistants for things, and take requests (owners approve anything shared)" },
  { key: "customMcp", label: "Own MCP servers", hint: "Add private MCP servers for their own assistant (company-wide servers are always available)" },
];

const CHAT_APPS = [
  ["slack", "Slack"],
  ["discord", "Discord"],
  ["whatsapp", "WhatsApp"],
] as const;

const TABS = [
  ["overview", "Overview"],
  ["access", "Access"],
  ["knowledge", "Knowledge sharing"],
  ["budget", "Budget & schedule"],
] as const;

/**
 * Admin → People & groups → one group. Everything about the group in one place, in four tabs: who is in
 * it, what their assistants may use, how far what they learn may travel, and their budget and briefing times.
 */
export function GroupPage() {
  const { id = "" } = useParams();
  const groups = useGroups();
  const tabsRef = useActiveInView<HTMLElement>(`${useLocation().pathname}:${groups.isPending}`);
  if (groups.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (groups.error) return <ErrorText error={groups.error} />;
  const g = groups.data.find((x) => x.id === id);
  if (!g)
    return (
      <Card className="p-6 text-sm">
        That group doesn't exist any more.{" "}
        <Link to="/admin/people/groups" className="text-[var(--color-accent)] hover:underline">
          See all groups
        </Link>
      </Card>
    );
  return (
    <>
      <Link to="/admin/people/groups" className="mb-3 inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline">
        <Icon name="arrowLeft" size={14} /> All groups
      </Link>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold tracking-tight">{g.name}</h1>
        {g.isDefault && <Badge tone="accent">default</Badge>}
      </div>
      <p className="mb-5 text-sm text-[var(--muted)]">
        {g.members} {g.members === 1 ? "member" : "members"}
        {g.description && ` · ${g.description}`}
      </p>
      <nav ref={tabsRef} aria-label="Group settings" className="mb-6 flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-[rgb(20_20_20/0.05)] p-1 [scrollbar-width:none] sm:w-fit [&::-webkit-scrollbar]:hidden">
        {TABS.map(([path, label]) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) => cx(segItem(isActive), "shrink-0 px-3.5 py-1.5 text-[13px]")}
          >
            {label}
          </NavLink>
        ))}
      </nav>
      {/* Re-mount per group: each tab's form starts from that group's saved values. */}
      <Outlet key={g.id} context={g} />
    </>
  );
}

const useGroup = () => useOutletContext<Group>();

/** Saves part of the group. The API takes the whole group, so the rest goes as it is saved now. */
function useSaveGroup(g: Group) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<GroupInput>) =>
      unwrap(api.admin.groups[":id"].$put({ param: { id: g.id }, json: { name: g.name, description: g.description, quotas: g.quotas, permissions: g.permissions, isDefault: g.isDefault, ...patch } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      void qc.invalidateQueries({ queryKey: ["briefings"] });
    },
  });
}

function SaveBar({ save, dirty, invalid }: { save: { isPending: boolean; isSuccess: boolean; error: unknown }; dirty: boolean; invalid?: boolean }) {
  return (
    <>
      <ErrorText error={save.error} />
      <div className="flex items-center justify-end gap-3">
        {save.isSuccess && !dirty && <span className="text-sm text-[var(--muted)]">Saved</span>}
        <Button type="submit" variant="primary" disabled={save.isPending || !dirty || invalid}>
          Save
        </Button>
      </div>
    </>
  );
}

/* ── Overview: name, default, members ─────────────────────────────────── */

export function GroupOverviewTab() {
  const g = useGroup();
  const qc = useQueryClient();
  const nav = useNavigate();
  const save = useSaveGroup(g);
  const [form, setForm] = useState({ name: g.name, description: g.description, isDefault: g.isDefault });
  const remove = useMutation({
    mutationFn: () => unwrap(api.admin.groups[":id"].$delete({ param: { id: g.id } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      nav("/admin/people/groups");
    },
  });
  const users = useQuery({ queryKey: ["users"], queryFn: () => unwrap(api.admin.users.$get()) });
  const members = users.data?.filter((u) => u.groupId === g.id) ?? [];
  const dirty = form.name.trim() !== g.name || form.description !== g.description || form.isDefault !== g.isDefault;
  const apps = g.permissions.composioToolkits;
  const chat = CHAT_APPS.filter(([m]) => g.permissions.messaging[m]).map(([, label]) => label);

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ ...form, name: form.name.trim() });
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input required maxLength={64} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Description">
              <Input maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
          </div>
          <Switch
            label="Default group"
            hint={g.isDefault ? "New users join this group. To change that, make another group the default." : "New users join this group automatically."}
            checked={form.isDefault}
            disabled={g.isDefault}
            onChange={(v) => setForm({ ...form, isDefault: v })}
          />
          <SaveBar save={save} dirty={dirty} />
        </form>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard to="../access" title="Access">
          {CAPABILITIES.filter((t) => g.permissions[t.key] === true).map((t) => t.label).join(", ") || "Nothing beyond chat"}
          <br />
          Apps: {apps.includes("*") ? "all" : apps.length ? `${apps.length} chosen` : "none"} · Chat apps: {chat.join(", ") || "none"}
        </SummaryCard>
        <SummaryCard to="../knowledge" title="Knowledge sharing">
          Memories: {LEVEL_LABEL[g.permissions.promoteMemory].toLowerCase()}
          <br />
          Skills: {LEVEL_LABEL[g.permissions.publishSkills].toLowerCase()}
        </SummaryCard>
        <SummaryCard to="../budget" title="Budget & schedule">
          {g.quotas.monthlyBudgetUsd === null ? "No monthly AI budget limit" : `$${g.quotas.monthlyBudgetUsd} a month per person`}
          <br />
          {g.quotas.storageGb} GB storage · {fmtGb(g.quotas.memoryMb)} assistant memory
        </SummaryCard>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line)] px-6 py-4">
          <h2 className="text-base font-semibold">Members</h2>
          <Link to="/admin/people/users" className="inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline">
            Add or move people <Icon name="arrowRight" size={14} />
          </Link>
        </div>
        {users.isPending ? (
          <div className="grid place-items-center py-8"><Spinner /></div>
        ) : users.error ? (
          <div className="p-4"><ErrorText error={users.error} /></div>
        ) : !members.length ? (
          <p className="px-6 py-6 text-sm text-[var(--muted)]">Nobody is in this group yet. Move people here from Users.</p>
        ) : (
          <ul className="max-h-96 divide-y divide-[var(--line)] overflow-y-auto">
            {members.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-2 px-6 py-2.5 text-sm">
                <span className="font-medium">{u.name}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-[var(--muted)]">{u.email}</span>
                {u.role === "admin" && <Badge tone="accent">admin</Badge>}
                {u.status !== "active" && <Badge tone="bad">{u.status}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {!g.isDefault && (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-6">
          <div>
            <h2 className="text-base font-semibold">Delete this group</h2>
            <p className="mt-1 text-sm text-[var(--muted)]">Its members move to the default group, and take on its access and budget.</p>
          </div>
          <Button variant="danger" disabled={remove.isPending} onClick={() => confirm(`Delete ${g.name}? Its members move to the default group.`) && remove.mutate()}>
            Delete group
          </Button>
          <div className="w-full"><ErrorText error={remove.error} /></div>
        </Card>
      )}
    </div>
  );
}

const fmtGb = (mb: number) => `${(mb / 1024).toFixed(mb % 1024 ? 1 : 0)} GB`;

function SummaryCard({ to, title, children }: { to: string; title: string; children: React.ReactNode }) {
  return (
    <Link to={to} relative="path" className="block rounded-xl border border-[var(--line)] bg-[var(--panel)] p-4 transition hover:border-[var(--color-accent)]">
      <div className="mb-1 flex items-center justify-between text-sm font-semibold">
        {title} <Icon name="arrowRight" size={15} className="text-[var(--color-accent)]" />
      </div>
      <p className="text-xs leading-relaxed text-[var(--muted)]">{children}</p>
    </Link>
  );
}

/* ── Access: capabilities, connected-app allowlist, chat apps ─────────── */

/** The Access tab's part of the permissions (the two sharing levels belong to Knowledge sharing). */
const accessOf = (p: Permissions) => ({
  terminal: p.terminal,
  browser: p.browser,
  webTools: p.webTools,
  cronJobs: p.cronJobs,
  subagents: p.subagents,
  fileUpload: p.fileUpload,
  colleagueRequests: p.colleagueRequests,
  customMcp: p.customMcp,
  webApps: p.webApps,
  publicApps: p.publicApps,
  composioToolkits: p.composioToolkits,
  messaging: p.messaging,
});
type Access = ReturnType<typeof accessOf>;

export function GroupAccessTab() {
  const g = useGroup();
  const cfg = usePublicConfig();
  const save = useSaveGroup(g);
  const [form, setForm] = useState<Access>(() => accessOf(g.permissions));
  const set = <K extends keyof Access>(k: K, v: Access[K]) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(accessOf(g.permissions));
  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ permissions: { ...g.permissions, ...form } });
      }}
    >
      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold">What their assistants may use</h2>
        <p className="mb-3 text-sm text-[var(--muted)]">Enforced when each assistant is set up, never left to the assistant. Changes reach members' assistants within a few minutes.</p>
        <div className="grid gap-x-8 sm:grid-cols-2">
          {CAPABILITIES.map((t) => (
            <Switch key={t.key} label={t.label} hint={t.hint} checked={form[t.key as keyof Access] as boolean} onChange={(v) => set(t.key as keyof Access, v as never)} />
          ))}
        </div>
      </Card>
      {cfg.data?.appsDomain && (
        <Card className="p-6">
          <h2 className="mb-1 text-base font-semibold">Web apps</h2>
          <p className="mb-3 text-sm text-[var(--muted)]">Small apps members' assistants build and publish at *.{cfg.data.appsDomain}, behind sign-in. They need Terminal &amp; code.</p>
          <div className="grid gap-x-8 sm:grid-cols-2">
            <Switch label="Publish web apps" hint="New apps open only for their owner, who chooses who else may open them" checked={form.webApps} onChange={(v) => set("webApps", v)} />
            <Switch
              label="Share apps outside the company"
              hint="Let owners open an app to any Google account, or to anyone with a share link"
              checked={form.publicApps && form.webApps}
              disabled={!form.webApps}
              onChange={(v) => set("publicApps", v)}
            />
          </div>
        </Card>
      )}
      <Card className="p-6">
        <ToolkitPicker value={form.composioToolkits} onChange={(v) => set("composioToolkits", v)} />
      </Card>
      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold">Chat apps</h2>
        <p className="mb-3 text-sm text-[var(--muted)]">Members can talk to their assistant from these, once the bots are set up under Workspace → Messaging bots.</p>
        <div className="grid gap-x-8 sm:grid-cols-3">
          {CHAT_APPS.map(([m, label]) => (
            <Switch key={m} label={label} checked={form.messaging[m]} onChange={(v) => set("messaging", { ...form.messaging, [m]: v })} />
          ))}
        </div>
      </Card>
      <SaveBar save={save} dirty={dirty} />
    </form>
  );
}

/** Composio toolkits a group may connect: none, a chosen list, or everything ("*"). */
function ToolkitPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const mode = value.includes("*") ? "all" : value.length ? "some" : "none";
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["admin-toolkits", q],
    queryFn: () => unwrap(api.admin.composio.toolkits.$get({ query: { q } })),
    enabled: mode === "some",
    staleTime: 60_000,
  });
  const setMode = (m: string) => onChange(m === "all" ? ["*"] : m === "none" ? [] : value.filter((v) => v !== "*").length ? value.filter((v) => v !== "*") : ["gmail", "googlecalendar", "github"]);
  return (
    <section>
      <h2 className="mb-1 text-base font-semibold">Connected apps</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">Apps members may connect their own accounts to (via Composio). Each person's connections are private to them.</p>
      {search.data && !search.data.enabled && <p className="mb-2 text-xs text-amber-700">Composio isn't configured on this deployment, so this has no effect yet.</p>}
      <div className="mb-3 flex flex-wrap gap-2">
        {(
          [
            ["none", "Off"],
            ["some", "Selected apps"],
            ["all", "All apps"],
          ] as const
        ).map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cx("rounded-lg border px-3 py-1.5 text-sm transition", mode === m ? "border-[var(--color-accent)]/50 bg-[var(--color-brand)]/10 text-[var(--text)]" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "some" && (
        <>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {value.map((slug) => (
              <button key={slug} type="button" onClick={() => onChange(value.filter((v) => v !== slug))} className="inline-flex items-center gap-1 rounded-full bg-[var(--color-brand)]/12 py-1 pr-1.5 pl-2.5 font-mono text-xs text-[var(--color-accent)] hover:bg-[var(--color-brand)]/20" title="Remove">
                {slug} <Icon name="x" size={12} />
              </button>
            ))}
          </div>
          <Input placeholder="Search apps to add (e.g. notion, slack, hubspot)" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="mt-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
            {search.data?.toolkits
              .filter((t) => !value.includes(t.slug))
              .map((t) => (
                <button key={t.slug} type="button" onClick={() => onChange([...value, t.slug])} className="flex items-center gap-1.5 rounded-lg border border-[var(--line)] px-2 py-1 text-xs hover:bg-[var(--panel-2)]">
                  {t.logo && <img src={t.logo} alt="" className="h-4 w-4 object-contain" />}
                  {t.name}
                </button>
              ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ── Knowledge sharing: the two tiers and the group's own rules ───────── */

type Knowledge = Awaited<ReturnType<typeof loadKnowledge>>;
const loadKnowledge = (id: string) => unwrap(api.admin.groups[":id"].knowledge.$get({ param: { id } }));

export function GroupKnowledgeTab() {
  const g = useGroup();
  const q = useQuery({ queryKey: ["group-knowledge", g.id], queryFn: () => loadKnowledge(g.id) });
  // The editor re-mounts on fresh data (so the form always starts from what is saved); "Saved" outlives that.
  const [saved, setSaved] = useState(false);
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  return <KnowledgeEditor key={JSON.stringify(q.data)} id={g.id} data={q.data} saved={saved} onSaved={setSaved} />;
}

function KnowledgeEditor({ id, data, saved, onSaved }: { id: string; data: Knowledge; saved: boolean; onSaved: (v: boolean) => void }) {
  const qc = useQueryClient();
  const initial: GroupKnowledgeInput = { promoteMemory: data.promoteMemory, publishSkills: data.publishSkills, memoryRules: data.memoryRules, skillRules: data.skillRules };
  const [form, setForm] = useState<GroupKnowledgeInput>(initial);
  const set = (p: Partial<GroupKnowledgeInput>) => {
    onSaved(false);
    setForm({ ...form, ...p });
  };
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.groups[":id"].knowledge.$put({ param: { id }, json: form })),
    onSuccess: () => {
      onSaved(true);
      void qc.invalidateQueries({ queryKey: ["group-knowledge", id] });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      void qc.invalidateQueries({ queryKey: ["memory"] });
      void qc.invalidateQueries({ queryKey: ["skills-admin"] });
    },
  });
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-[var(--muted)]">
        What members' assistants learn can be shared in two tiers: within {data.group.name}, or on to the whole company. This tab sets how far this group goes and what stays with the team; the
        company-wide rules are under{" "}
        <Link to="/admin/knowledge/rules" className="text-[var(--color-accent)] hover:underline">
          Knowledge → Sharing rules
        </Link>
        .
      </p>
      <TierCard
        title="Memories"
        count={`${data.stats.memories} shared within the group`}
        intro="Work knowledge the assistants form is checked against the rules and shared, rewritten without personal detail. A member can take any of theirs back from their Memory page."
        kind="memory"
        level={form.promoteMemory}
        onLevel={(v) => set({ promoteMemory: v })}
        rules={form.memoryRules}
        onRules={(v) => set({ memoryRules: v })}
        defaultRules={DEFAULT_GROUP_MEMORY_RULES}
        rulesTitle="What stays within the group"
        rulesHint="Plain language for the AI that decides, per memory, whether colleagues outside the group would need it. Anything the company rules forbid is never shared at either tier."
      />
      <TierCard
        title="Skills"
        count={`${data.stats.skills} shared within the group`}
        intro="Skills the assistants save are checked for sensitive data first (the company's sensitivity rules), then shared. Members can keep any skill private; you can move a skill between tiers under Knowledge → Skills."
        kind="skills"
        level={form.publishSkills}
        onLevel={(v) => set({ publishSkills: v })}
        rules={form.skillRules}
        onRules={(v) => set({ skillRules: v })}
        defaultRules={DEFAULT_GROUP_SKILL_RULES}
        rulesTitle="Which skills stay within the group"
        rulesHint="Plain language for the AI that decides, per clean skill, whether it is about this team's own work or something any colleague could use."
      />
      <ErrorText error={save.error} />
      <div className="flex items-center justify-end gap-3">
        {saved && !dirty && <span className="text-sm text-[var(--muted)]">Saved</span>}
        <Button type="submit" variant="primary" disabled={save.isPending || !dirty}>
          Save
        </Button>
      </div>
    </form>
  );
}

function TierCard(p: {
  title: string;
  count: string;
  intro: string;
  kind: "memory" | "skills";
  level: ShareLevel;
  onLevel: (v: ShareLevel) => void;
  rules: string;
  onRules: (v: string) => void;
  defaultRules: string;
  rulesTitle: string;
  rulesHint: string;
}) {
  const rulesApply = p.level === "company";
  return (
    <Card className="p-6">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">{p.title}</h2>
        <Badge>{p.count}</Badge>
      </div>
      <p className="mb-4 text-sm text-[var(--muted)]">{p.intro}</p>
      <h3 className="mb-2 text-sm font-medium">How far they may go</h3>
      <LevelPicker kind={p.kind} value={p.level} onChange={p.onLevel} />
      <div className="mt-6 border-t border-[var(--line)] pt-5">
        <h3 className="text-sm font-medium">{p.rulesTitle}</h3>
        <p className="mb-3 mt-1 text-xs text-[var(--muted)]">
          {rulesApply
            ? p.rulesHint
            : p.level === "group"
              ? "Not needed while sharing stops at the group: everything shared stays within it. Kept for when you allow company-wide sharing."
              : "Not used while sharing is off. Kept for when you switch it on."}
        </p>
        <textarea
          rows={10}
          value={p.rules}
          onChange={(e) => p.onRules(e.target.value)}
          aria-label={p.rulesTitle}
          className={cx(
            "w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20",
            !rulesApply && "opacity-60",
          )}
        />
        <div className="mt-2 flex justify-end">
          <button type="button" className="text-sm text-[var(--color-accent)] hover:underline disabled:opacity-50" disabled={p.rules === p.defaultRules} onClick={() => p.onRules(p.defaultRules)}>
            Reset to default rules
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ── Budget & schedule: budget, storage, memory limits, briefing times ── */

export function GroupBudgetTab() {
  const g = useGroup();
  const cfg = usePublicConfig();
  const qc = useQueryClient();
  const save = useSaveGroup(g);
  const [quotas, setQuotas] = useState<Quotas>(g.quotas);
  const [unlimited, setUnlimited] = useState(g.quotas.monthlyBudgetUsd === null);
  const quota = (k: keyof Quotas, v: number | null) => setQuotas((q) => ({ ...q, [k]: v }));
  const next = { ...quotas, monthlyBudgetUsd: unlimited ? null : quotas.monthlyBudgetUsd ?? 0 };
  const dirty = JSON.stringify(next) !== JSON.stringify(g.quotas);
  const briefings = useQuery({ queryKey: ["briefings"], queryFn: () => unwrap(api.admin.briefings.$get()) });
  const row = briefings.data?.groups.find((x) => x.id === g.id);

  return (
    <div className="space-y-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ quotas: next });
        }}
        className="space-y-4"
      >
        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold">Budget & resources</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Monthly AI budget per person (USD)" hint="Requests are refused once a person reaches this.">
              <div className="flex items-center gap-3">
                <Input type="number" min={0} step="0.01" required={!unlimited} disabled={unlimited} value={unlimited ? "" : quotas.monthlyBudgetUsd ?? ""} onChange={(e) => quota("monthlyBudgetUsd", e.target.value === "" ? null : Number(e.target.value))} />
                <label className="flex shrink-0 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => {
                      setUnlimited(e.target.checked);
                      if (!e.target.checked && quotas.monthlyBudgetUsd === null) quota("monthlyBudgetUsd", 20);
                    }}
                  />
                  Unlimited
                </label>
              </div>
            </Field>
            <Field label="Storage per person (GB)" hint="Size of each person's private disk.">
              <Input type="number" required min={1} max={1024} value={quotas.storageGb} onChange={(e) => quota("storageGb", Number(e.target.value))} />
            </Field>
            <Field label="Assistant memory (MB)" hint="Hard limit for the assistant's process.">
              <Input type="number" required min={256} max={16384} step={128} value={quotas.memoryMb} onChange={(e) => quota("memoryMb", Number(e.target.value))} />
            </Field>
            <Field label="Sandbox memory (MB)" hint="Hard limit for commands the assistant runs. An overflow stops the command, not the assistant.">
              <Input type="number" required min={256} max={16384} step={128} value={quotas.sandboxMemoryMb} onChange={(e) => quota("sandboxMemoryMb", Number(e.target.value))} />
            </Field>
            {cfg.data?.appsDomain && (
              <Field label="Web apps memory (MB)" hint="Hard limit shared by a person's published web apps.">
                <Input type="number" required min={128} max={16384} step={128} value={quotas.appsMemoryMb} onChange={(e) => quota("appsMemoryMb", Number(e.target.value))} />
              </Field>
            )}
          </div>
        </Card>
        <SaveBar save={save} dirty={dirty} />
      </form>

      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold">Briefings</h2>
        <p className="mb-4 text-sm text-[var(--muted)]">
          When members' daily briefing and weekly retrospective go out, in each person's own time zone. People can pick their own times or mute theirs under Schedules &amp; briefings. What briefings say
          is set under{" "}
          <Link to="/admin/assistants/briefings" className="text-[var(--color-accent)] hover:underline">
            Assistants → Briefings
          </Link>
          .
        </p>
        {briefings.isPending ? (
          <div className="grid place-items-center py-6"><Spinner /></div>
        ) : briefings.error ? (
          <ErrorText error={briefings.error} />
        ) : !row ? null : (
          <>
            {!briefings.data.settings.enabled && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">Daily briefings are switched off for the whole company, so these times aren't used for now.</p>}
            <GroupBriefingForm
              key={JSON.stringify(row.briefing)}
              groupId={g.id}
              initial={row.briefing}
              weeklyOn={briefings.data.settings.weekly}
              onSaved={() => void qc.invalidateQueries({ queryKey: ["briefings"] })}
            />
          </>
        )}
      </Card>
    </div>
  );
}
